import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, MapPin, Save, Check, Building2 } from "lucide-react";
import { toast } from "sonner";
import { GeofenceWizardMap, AddressSearch } from "@/components/GeofenceWizard";
import { usePlanLimits, planDisplayName } from "@/lib/plan-limits";
import { UpgradePrompt } from "@/components/UpgradePrompt";

export const Route = createFileRoute("/_authenticated/sites/new")({
  component: NewSitePage,
  head: () => ({ meta: [{ title: "New site — MissionReady" }] }),
});

function StepBadge({ n }: { n: number }) {
  return (
    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-[11px] font-bold text-background">
      {n}
    </span>
  );
}

function NewSitePage() {
  const { currentOrg, currentRole } = useAuth();
  const navigate = useNavigate();
  const canEdit = ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");
  const limits = usePlanLimits(currentOrg?.id ?? null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [radius, setRadius] = useState(150);
  const [busy, setBusy] = useState(false);

  const atSiteCap = limits.maxSites != null && limits.sitesUsed >= limits.maxSites;

  if (!canEdit) {
    return (
      <div className="px-4 py-10 text-center text-sm text-muted-foreground">
        You don't have permission to create sites.
      </div>
    );
  }

  const save = async () => {
    if (!currentOrg) return;
    if (!name.trim()) return toast.error("Give the site a name");
    if (!center) return toast.error("Search an address and drop a pin first");
    if (atSiteCap) { setUpgradeOpen(true); return; }
    setBusy(true);
    const { data: site, error } = await supabase.from("sites").insert({
      organization_id: currentOrg.id,
      name: name.trim(),
      address: address || null,
      latitude: center.lat,
      longitude: center.lng,
    }).select("id").single();
    if (error || !site) { setBusy(false); return toast.error(error?.message ?? "Could not create site"); }
    await supabase.from("geofences").insert({
      organization_id: currentOrg.id,
      site_id: site.id,
      name: `${name.trim()} perimeter`,
      kind: "inclusion",
      center_lat: center.lat,
      center_lng: center.lng,
      radius_m: Math.round(radius),
      active: true,
    });
    setBusy(false);
    toast.success("Site & perimeter saved");
    navigate({ to: "/sites" });
  };

  return (
    <div className="min-h-dvh bg-muted/40">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-foreground text-background">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4">
          <Link to="/sites" className="rounded-md p-1 hover:bg-background/10" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <MapPin className="h-5 w-5" />
          <h1 className="font-display text-xl font-bold">Sites &amp; Geofences</h1>
        </div>
        <p className="mx-auto max-w-3xl px-4 pb-4 text-sm text-background/70">
          Search an address, drop the pin, adjust the radius, then save.
        </p>
      </header>

      <div className="mx-auto max-w-3xl space-y-4 px-4 py-5">
        {atSiteCap && (
          <div className="rounded-2xl border border-primary/40 bg-primary/5 p-4 text-sm">
            <div className="font-semibold">You've hit your site limit</div>
            <p className="mt-1 text-muted-foreground">
              {planDisplayName(limits.planKey)} includes {limits.maxSites} site
              {limits.maxSites === 1 ? "" : "s"}. Upgrade to add more.
            </p>
            <div className="mt-3">
              <Button asChild size="sm"><Link to="/billing">See plans</Link></Button>
            </div>
          </div>
        )}

        {/* Site name */}
        <section className="rounded-2xl border border-border bg-card p-4">
          <Label htmlFor="site-name" className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <Building2 className="h-3.5 w-3.5" /> Site
          </Label>
          <Input
            id="site-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Main Campus — Building A"
            className="h-11 text-base"
          />
        </section>

        {/* Address search */}
        <section className="rounded-2xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center gap-2">
            <StepBadge n={1} />
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Address search</span>
          </div>
          <AddressSearch
            onPick={(lat, lng, label) => {
              setCenter({ lat, lng });
              setAddress(label);
            }}
          />
          {address && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-border bg-background/60 p-3 text-sm">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              <div>
                <div className="font-semibold">Address located</div>
                <div className="text-muted-foreground">{address}</div>
              </div>
            </div>
          )}
        </section>

        {/* Map editor */}
        <section className="rounded-2xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center gap-2">
            <StepBadge n={2} />
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Map editor <span className="font-normal normal-case text-muted-foreground/80">— tap or drag pin to move</span>
            </span>
          </div>
          <GeofenceWizardMap
            center={center}
            radius={radius}
            onChange={({ lat, lng, radius: r }) => { setCenter({ lat, lng }); setRadius(r); }}
            height={420}
          />
        </section>

        {/* Save */}
        <section className="rounded-2xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center gap-2">
            <StepBadge n={3} />
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Save</span>
          </div>
          <Button
            type="button"
            onClick={save}
            disabled={busy || !center || !name.trim() || atSiteCap}
            className="h-12 w-full gap-2 text-base"
          >
            <Save className="h-4 w-4" /> {busy ? "Saving…" : atSiteCap ? "Upgrade to add another site" : "Save Geofence"}
          </Button>
          {!center && !atSiteCap && (
            <p className="mt-2 text-center text-xs text-muted-foreground">
              Search an address above to drop the pin first.
            </p>
          )}
        </section>
      </div>

      <UpgradePrompt
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        planName={planDisplayName(limits.planKey)}
        reason={`You can have up to ${limits.maxSites} site${limits.maxSites === 1 ? "" : "s"} on this plan and you've already reached that. Pick a plan to continue adding sites.`}
        returnTo="/sites/new"
      />
    </div>
  );
}
